import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { environment } from '../../../environments/environment';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  username?: string;
  streetAddressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  avatarUrl?: string;
  emailVerified?: boolean;
  is2faEnabled: boolean;
  twoFactorMethod?: string;
  onboardingStatus?: string;
  language?: string;
  timezone?: string;
}

/**
 * Admin Profile Page Component
 * Read-only view of the current admin user's profile information.
 * Fetches data from GET /auth/me (same as settings page).
 */
@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, PhoneFormatPipe],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit {
  profile: UserProfile | null = null;
  loading = true;
  errorMessage = '';

  constructor(private http: HttpClient) {}

  async ngOnInit(): Promise<void> {
    await this.loadProfile();
  }

  async loadProfile(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.get<{ profile: UserProfile }>(`${environment.apiUrl}/auth/me`)
      );

      if (response?.profile) {
        this.profile = response.profile;
      }
    } catch (error: any) {
      console.error('Load profile error:', error);
      this.errorMessage = 'Failed to load profile information';
    } finally {
      this.loading = false;
    }
  }

  formatDate(date: string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  get2FAMethodLabel(): string {
    if (!this.profile?.is2faEnabled) {
      return 'Not enabled';
    }

    switch (this.profile.twoFactorMethod) {
      case '2fa-totp':
        return 'Authenticator App';
      case '2fa-sms':
        return 'SMS Text Message';
      default:
        return 'Unknown';
    }
  }
}
